import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";
import { refreshSiteText } from "@/hooks/useSiteImages";

export default function SiteTextAdmin() {
  const [slots, setSlots] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/site-text").then((r) => { setSlots(r.data); setValues(Object.fromEntries(r.data.map((s) => [s.key, s.value || ""]))); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-text", { values }); toast.success("Website text saved — live now"); refreshSiteText(); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resetOne = (k) => setValues({ ...values, [k]: "" });
  const grouped = useMemo(() => { const g = new Map(); slots.forEach((s) => g.set(s.section, [...(g.get(s.section) || []), s])); return [...g.entries()]; }, [slots]);
  const dirty = slots.some((s) => (s.value || "") !== (values[s.key] || ""));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-muted-foreground">Edit the wording of the home page. Leave a field blank to use the built-in text (shown as the placeholder). Saves go live immediately.</p>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="h-3.5 w-3.5 mr-1" />View website</Button></a>
          <Button size="sm" data-testid="site-text-save-btn" onClick={save} disabled={saving || !dirty} className="rounded-full bg-coral hover:bg-coral-hover text-white"><Save className="h-3.5 w-3.5 mr-1" />{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </div>
      <div className="space-y-6">
        {grouped.map(([sec, secSlots]) => (
          <div key={sec} className="bg-cream-card border border-cream-deep rounded-xl p-5">
            <h4 className="font-heading font-bold text-brand mb-4">{sec}</h4>
            <div className="space-y-4">
              {secSlots.map((s) => (
                <div key={s.key} data-testid={`site-text-${s.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-muted-foreground">{s.label}</label>
                    {values[s.key] && <button type="button" data-testid={`site-text-reset-${s.key}`} onClick={() => resetOne(s.key)} className="text-[11px] text-brand inline-flex items-center gap-1 hover:underline"><RotateCcw className="h-3 w-3" />Use built-in</button>}
                  </div>
                  {s.multiline
                    ? <Textarea data-testid={`site-text-input-${s.key}`} rows={3} value={values[s.key] || ""} placeholder={DEFAULTS[s.key] || ""} onChange={(e) => setValues({ ...values, [s.key]: e.target.value })} />
                    : <Input data-testid={`site-text-input-${s.key}`} value={values[s.key] || ""} placeholder={DEFAULTS[s.key] || ""} onChange={(e) => setValues({ ...values, [s.key]: e.target.value })} />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEFAULTS = {
  hero_eyebrow: "Oral healthcare compliance, from dental care professionals",
  hero_title: "Could your care home evidence good oral healthcare to the",
  hero_highlight: "CQC tomorrow?",
  hero_tagline: "Training, compliance and expert support for care homes — all in one place.",
  oc_eyebrow: "The flagship · included with every licence",
  oc_title: "Turn one of your team into an Oral Health Champion",
  oc_body: "Training only works if it’s used on the floor. Your Oral Health Champion makes sure it is: promoting good mouth care, supporting colleagues, spotting concerns early and keeping oral health on the agenda. With live training and ongoing support from KOHC, they’ll know what to do and when to ask.",
  experts_eyebrow: "Who you're learning from",
  experts_title: "Meet Your Oral Healthcare Expert",
  experts_name: "Jane Peterson BSc, RDH",
  experts_credential: "Registered Dental Hygienist | GDC Registration No. 4846",
  experts_role: "Founder of Knowledge Oral HealthCare (KOHC)",
  experts_body1: "With over a decade of experience specialising in oral healthcare within the care sector, Jane combines her clinical background as a registered dental hygienist with a passion for improving oral healthcare standards in care settings.",
  experts_body2: "Since 2015, she has worked with care professionals to develop practical, accredited training that builds staff knowledge, skills and confidence.",
  experts_body3: "Through KOHC, Jane now brings together staff training, live Oral Health Champion development, compliance resources, audit tools and ongoing professional support to help care providers deliver and demonstrate good oral healthcare.",
  features_title: "Everything your care home needs for oral-health compliance — in one place.",
  testimonials_title: "Homes that made oral health effortless",
  tt_title: "Become an Oral Health Train-the-Trainer",
  tt_body: "A separate, application-gated professional qualification. Deliver oral-health training in your own organisation — and optionally join the KOHC trainer roster to deliver live sessions.",
  cta_title: "Ready to fix your oral-health compliance?",
  cta_body: "Start a free trial today, add your team, and see your compliance dashboard fill in real time.",
};
